import {
  type ColumnDef,
  type ColumnFiltersState,
  type RowSelectionState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react"
import * as React from "react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { TableSkeleton } from "./loading-skeletons"
import { cn } from "@/lib/utils"

export type DataTableProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  isLoading?: boolean
  empty?: React.ReactNode
  toolbar?: (table: ReturnType<typeof useReactTable<TData>>) => React.ReactNode
  pageSize?: number
  enableSelection?: boolean
  className?: string
  onRowClick?: (row: TData) => void
  rowKey?: (row: TData) => string
}

export function DataTable<TData, TValue>({
  columns,
  data,
  isLoading,
  empty,
  toolbar,
  pageSize = 20,
  enableSelection = false,
  className,
  onRowClick,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})

  const effectiveColumns = React.useMemo<ColumnDef<TData, TValue>[]>(() => {
    if (!enableSelection) return columns
    const selectCol: ColumnDef<TData, TValue> = {
      id: "__select",
      header: ({ table }) => (
        <Checkbox
          aria-label="Tümünü seç"
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
                ? "indeterminate"
                : false
          }
          onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          aria-label="Satırı seç"
          checked={row.getIsSelected()}
          onCheckedChange={(v) => row.toggleSelected(!!v)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 32,
    }
    return [selectCol, ...columns]
  }, [columns, enableSelection])

  const table = useReactTable({
    data,
    columns: effectiveColumns,
    state: { sorting, columnFilters, rowSelection },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
    enableRowSelection: enableSelection,
  })

  return (
    <div className={cn("space-y-3", className)}>
      {toolbar?.(table)}
      <div className="overflow-hidden rounded-xl border bg-card">
        {isLoading ? (
          <TableSkeleton rows={6} columns={effectiveColumns.length} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id} className="border-b">
                    {hg.headers.map((header) => {
                      const canSort = header.column.getCanSort()
                      return (
                        <th
                          key={header.id}
                          className="px-4 py-3 font-medium"
                          style={{
                            width: header.getSize() !== 150 ? header.getSize() : undefined,
                          }}
                        >
                          {header.isPlaceholder ? null : canSort ? (
                            <button
                              type="button"
                              onClick={header.column.getToggleSortingHandler()}
                              className="inline-flex items-center gap-1.5 hover:text-foreground"
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              <ArrowUpDown
                                className={cn(
                                  "size-3",
                                  header.column.getIsSorted() && "text-foreground",
                                )}
                              />
                            </button>
                          ) : (
                            flexRender(header.column.columnDef.header, header.getContext())
                          )}
                        </th>
                      )
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={effectiveColumns.length} className="p-0">
                      {empty ?? (
                        <div className="p-12 text-center text-sm text-muted-foreground">
                          Kayıt yok.
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className={cn(
                        "border-b transition-colors last:border-b-0",
                        onRowClick && "cursor-pointer hover:bg-muted/30",
                        row.getIsSelected() && "bg-primary-soft/50",
                      )}
                      onClick={() => onRowClick?.(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-4 py-3 align-middle">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {!isLoading && data.length > pageSize && (
        <DataTablePagination table={table} />
      )}
    </div>
  )
}

function DataTablePagination<TData>({
  table,
}: {
  table: ReturnType<typeof useReactTable<TData>>
}) {
  const pageIndex = table.getState().pagination.pageIndex
  const pageCount = table.getPageCount()
  return (
    <div className="flex items-center justify-between gap-3 px-1 text-sm">
      <div className="text-xs text-muted-foreground">
        Sayfa <span className="font-medium text-foreground">{pageIndex + 1}</span>{" "}
        / {pageCount} · Toplam {table.getFilteredRowModel().rows.length} kayıt
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          <ChevronLeft className="size-3.5" /> Önceki
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          Sonraki <ChevronRight className="size-3.5" />
        </Button>
      </div>
    </div>
  )
}
